import type { DesignTokens } from './tokens';
import type { GlassSettings } from '../utils/localStorage';

/**
 * v3 Glass×Material frosted-glass recipe (guide `.glass` / `.glass-2`).
 * Reserved for floating surfaces (bars, dialogs, sheets, menus, popovers) —
 * content cards stay solid so scrolling feeds never pay backdrop-filter cost.
 */
export const glassSx = (tokens: DesignTokens, level: 1 | 2 | 3 = 1) => {
  const bg =
    level === 3 ? tokens.colors.glass3 : level === 2 ? tokens.colors.glass2 : tokens.colors.glass1;
  return {
    backgroundColor: bg,
    backdropFilter: tokens.effects.glassBlur,
    WebkitBackdropFilter: tokens.effects.glassBlur,
    border: `1px solid ${tokens.colors.glassLine}`,
    boxShadow: `${tokens.colors.glassShadow}, inset 0 1px 0 ${tokens.colors.glassSpec}`,
  } as const;
};

// Glass bg1 tint bases (see primitives.colors.glass.*.bg1) — the user-tunable
// opacity slider supplies the alpha at runtime.
const GLASS_TINT_RGB = { light: '255,255,255', dark: '24,24,27' } as const;

/**
 * User-tunable frosted-glass surface for post cards. Unlike the fixed `.glass`
 * recipe above, blur and tint alpha come from the device-local glass settings
 * (Settings → Glass effect). Returns null when the effect is disabled so
 * callers can fall back to the solid card recipe.
 */
export const postGlassSx = (
  tokens: DesignTokens,
  mode: 'light' | 'dark',
  settings: GlassSettings
) => {
  if (!settings.enabled) return null;
  const filter = settings.blur > 0 ? `blur(${settings.blur}px) saturate(1.5)` : 'none';
  return {
    backgroundColor: `rgba(${GLASS_TINT_RGB[mode]},${settings.opacity / 100})`,
    backdropFilter: filter,
    WebkitBackdropFilter: filter,
    border: `1px solid ${tokens.colors.glassLine}`,
    boxShadow: `${tokens.colors.glassShadow}, inset 0 1px 0 ${tokens.colors.glassSpec}`,
  } as const;
};
